import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BarChart3, BriefcaseBusiness, ListChecks, Menu, Settings, Activity, Euro, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { partnerLogout } from "@/integrations/php/client";

const PartnerMobileNav = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const primary = [
    { label: "Εργασίες", path: `/partner/${partnerId}/pipeline`, icon: BriefcaseBusiness },
    { label: "Leads", path: `/partner/${partnerId}/leads`, icon: ListChecks },
    { label: "Απόδοση", path: `/partner/${partnerId}/analytics`, icon: BarChart3 },
  ];

  const secondary = [
    { label: "Δραστηριότητα", description: "Ιστορικό leads και σημειώσεων", path: `/partner/${partnerId}/activity`, icon: Activity },
    { label: "Χρεώσεις", description: "Ανάλυση χρεώσεων ανά lead", path: `/partner/${partnerId}/billing`, icon: Euro },
    { label: "Ρυθμίσεις", description: "Προφίλ, ακτίνα και ειδοποιήσεις", path: `/partner/${partnerId}/settings`, icon: Settings },
  ];
  const moreActive = secondary.some((item) => location.pathname === item.path);

  const go = (path: string) => {
    setMoreOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-2 pb-[max(0.4rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur md:hidden"
        aria-label="Πλοήγηση συνεργάτη"
      >
        <div className="mx-auto grid max-w-lg grid-cols-4">
          {primary.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => go(item.path)}
                className={cn(
                  "flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[10px] font-semibold",
                  active ? "bg-sky-50 text-sky-700" : "text-slate-500",
                )}
                aria-current={active ? "page" : undefined}
              >
                <Icon className="h-5 w-5" /> {item.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={cn(
              "flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[10px] font-semibold",
              moreActive ? "bg-sky-50 text-sky-700" : "text-slate-500",
            )}
          >
            <Menu className="h-5 w-5" /> Περισσότερα
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
          <SheetHeader className="pr-8 text-left">
            <SheetTitle>Περισσότερα</SheetTitle>
            <SheetDescription>Λογαριασμός, ιστορικό και χρεώσεις.</SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-2">
            {secondary.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => go(item.path)}
                  className={cn(
                    "flex min-h-14 w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left",
                    active ? "border-sky-200 bg-sky-50 text-sky-800" : "border-slate-200 bg-white text-slate-800",
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span>
                    <span className="block text-sm font-semibold">{item.label}</span>
                    <span className="block text-xs text-slate-500">{item.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => void partnerLogout()}
            className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 text-sm font-semibold text-rose-700"
          >
            <LogOut className="h-4 w-4" /> Αποσύνδεση
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default PartnerMobileNav;
